import {
  getRoleChainRelationships,
  getSingleBoneChainNameForRole,
} from './roleChainRelationships';
import { getChainRoleAssignments } from './chainRoleAssignments';

export interface ManualMappingDraft {
  roleToBone: Record<string, string>;
  roleToConfidence: Record<string, number>;
  chains: Record<string, string[]>;
}

export interface ManualRoleChainSyncCallbacks {
  getShortestBonePath: (startBoneName: string, endBoneName: string) => string[] | null;
  setChainBones: (chainName: string, chainBoneNames: string[]) => boolean;
}

function clearRolesForBoneExcept(
  draft: ManualMappingDraft,
  boneName: string,
  keepRoleName: string | null,
): boolean {
  let didChange = false;

  for (const [assignedRole, assignedBoneName] of Object.entries(draft.roleToBone)) {
    if (assignedBoneName !== boneName) continue;
    if (keepRoleName !== null && assignedRole === keepRoleName) continue;

    delete draft.roleToBone[assignedRole];
    delete draft.roleToConfidence[assignedRole];
    didChange = true;
  }

  return didChange;
}

export function clearRolesForBoneFromDraft(
  draft: ManualMappingDraft,
  boneName: string,
): boolean {
  return clearRolesForBoneExcept(draft, boneName, null);
}

export function clearBoneAssignmentsFromDraft(
  draft: ManualMappingDraft,
  boneName: string,
): {
  didClearRoles: boolean;
  clearedChainNames: string[];
} {
  const didClearRoles = clearRolesForBoneExcept(draft, boneName, null);
  const clearedChainNames: string[] = [];

  for (const [chainName, chainBoneNames] of Object.entries(draft.chains)) {
    if (!chainBoneNames.includes(boneName)) continue;

    clearedChainNames.push(chainName);
    const nextChainBoneNames = chainBoneNames.filter(
      (chainBoneName) => chainBoneName !== boneName,
    );

    if (nextChainBoneNames.length === 0) {
      delete draft.chains[chainName];
    } else {
      draft.chains[chainName] = nextChainBoneNames;
    }
  }

  return {
    didClearRoles,
    clearedChainNames,
  };
}

export function assignRoleToDraft(
  draft: ManualMappingDraft,
  boneName: string,
  roleName: string,
): boolean {
  let didChange = clearRolesForBoneExcept(draft, boneName, roleName);

  const currentBoneForRole = draft.roleToBone[roleName];
  if (currentBoneForRole === boneName) {
    return didChange;
  }

  delete draft.roleToBone[roleName];
  delete draft.roleToConfidence[roleName];
  draft.roleToBone[roleName] = boneName;
  return true;
}

function assignChainRolesToDraft(
  draft: ManualMappingDraft,
  chainName: string,
  chainBoneNames: readonly string[],
): boolean {
  let didChange = false;

  for (const { boneIndex, roleName } of getChainRoleAssignments(
    chainName,
    chainBoneNames.length,
  )) {
    const boneName = chainBoneNames[boneIndex];
    if (!boneName) continue;
    didChange = assignRoleToDraft(draft, boneName, roleName) || didChange;
  }

  return didChange;
}

function resolveKnownChainEndpointBones(
  draft: ManualMappingDraft,
  chainName: string,
  startRole: string,
  endRole: string,
): { startBoneName: string | null; endBoneName: string | null } {
  const existingChain = draft.chains[chainName] ?? [];
  const fallbackStartBoneName = existingChain[0] ?? null;
  const fallbackEndBoneName =
    existingChain.length > 0 ? existingChain[existingChain.length - 1] : null;

  return {
    startBoneName: draft.roleToBone[startRole] ?? fallbackStartBoneName,
    endBoneName: draft.roleToBone[endRole] ?? fallbackEndBoneName,
  };
}

function syncChainFromKnownEndpoints({
  draft,
  chainName,
  startRole,
  endRole,
  callbacks,
  requiredBoneName = null,
}: {
  draft: ManualMappingDraft;
  chainName: string;
  startRole: string;
  endRole: string;
  callbacks: ManualRoleChainSyncCallbacks;
  requiredBoneName?: string | null;
}): boolean {
  const { startBoneName, endBoneName } = resolveKnownChainEndpointBones(
    draft,
    chainName,
    startRole,
    endRole,
  );
  if (!startBoneName || !endBoneName) return false;

  const chainBoneNames = callbacks.getShortestBonePath(startBoneName, endBoneName);
  if (!chainBoneNames || chainBoneNames.length === 0) return false;
  if (requiredBoneName && !chainBoneNames.includes(requiredBoneName)) return false;

  return callbacks.setChainBones(chainName, chainBoneNames);
}

export function syncRoleAssignmentDraft({
  draft,
  boneName,
  roleName,
  callbacks,
}: {
  draft: ManualMappingDraft;
  boneName: string;
  roleName: string;
  callbacks: ManualRoleChainSyncCallbacks;
}): boolean {
  let didChange = false;

  const singleBoneChainName = getSingleBoneChainNameForRole(roleName);
  if (singleBoneChainName) {
    didChange = callbacks.setChainBones(singleBoneChainName, [boneName]) || didChange;
  }

  for (const relationship of getRoleChainRelationships(roleName)) {
    if (relationship.kind === 'singleBone') continue;

    if (relationship.kind === 'intermediate') {
      didChange =
        syncChainFromKnownEndpoints({
          draft,
          chainName: relationship.chainName,
          startRole: relationship.startRole,
          endRole: relationship.endRole,
          callbacks,
          requiredBoneName: boneName,
        }) || didChange;
      continue;
    }

    didChange =
      syncChainFromKnownEndpoints({
        draft,
        chainName: relationship.chainName,
        startRole: relationship.startRole,
        endRole: relationship.endRole,
        callbacks,
      }) || didChange;
  }

  return didChange;
}

export function syncChainAssignmentDraft({
  draft,
  chainName,
}: {
  draft: ManualMappingDraft;
  chainName: string;
}): boolean {
  const chainBoneNames = draft.chains[chainName] ?? [];
  if (chainBoneNames.length === 0) return false;

  if (getChainRoleAssignments(chainName, chainBoneNames.length).length === 0) {
    return false;
  }

  return assignChainRolesToDraft(draft, chainName, chainBoneNames);
}
